import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  FileText,
  Upload,
  Clock,
  FolderOpen,
  Download,
  Send,
  Calendar,
  CheckCircle,
  XCircle,
  FileType,
  FileCode2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPTED_UPLOAD_TYPES = ".pdf,.doc,.docx,.txt,.md,.rtf,.csv,.xls,.xlsx,.png,.jpg,.jpeg";

const triggerDownload = async (url, fallbackName) => {
  const token = localStorage.getItem("token");
  try {
    const res = await axios.get(url, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const filename = (m && m[1]) || fallbackName || "download";
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Download failed");
    return false;
  }
};

const DocumentManagement = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("templates");
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    type: "quote",
    content: "",
    is_active: true,
  });

  // Uploads state
  const [uploads, setUploads] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    category: "general",
    description: "",
  });

  // Auto-send state
  const [scheduledDocs, setScheduledDocs] = useState([]);
  const [autoSendDialogOpen, setAutoSendDialogOpen] = useState(false);
  const [autoSendForm, setAutoSendForm] = useState({
    template_id: "",
    recipient_type: "client",
    frequency: "monthly",
    next_send_date: "",
    is_active: true,
  });

  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, uploadsRes, scheduledRes] = await Promise.all([
        axios.get(`${API}/templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/documents/uploads`).catch(() => ({ data: [] })),
        axios.get(`${API}/documents/scheduled`).catch(() => ({ data: [] })),
      ]);
      
      setTemplates(templatesRes.data || []);
      setUploads(uploadsRes.data?.documents || uploadsRes.data || []);
      setScheduledDocs(scheduledRes.data?.schedules || scheduledRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Template handlers
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/templates`, templateForm);
      toast.success("Template created successfully");
      setTemplateDialogOpen(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      await axios.delete(`${API}/templates/${template.id}`);
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({ name: "", type: "quote", content: "", is_active: true });
    setSelectedTemplate(null);
  };

  // Upload handlers
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", uploadForm.title || selectedFile.name);
      formData.append("category", uploadForm.category);
      formData.append("description", uploadForm.description);
      
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ title: "", category: "general", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUpload = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await axios.delete(`${API}/documents/uploads/${doc.id}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Auto-send handlers
  const handleCreateAutoSend = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/documents/scheduled`, autoSendForm);
      toast.success("Auto-send schedule created");
      setAutoSendDialogOpen(false);
      setAutoSendForm({
        template_id: "",
        recipient_type: "client",
        frequency: "monthly",
        next_send_date: "",
        is_active: true,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create schedule");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoSend = async (schedule) => {
    try {
      await axios.put(`${API}/documents/scheduled/${schedule.id}`, {
        is_active: !schedule.is_active,
      });
      toast.success(`Schedule ${schedule.is_active ? "paused" : "activated"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      quote: "bg-blue-500",
      invoice: "bg-green-500",
      contract: "bg-purple-500",
      report: "bg-orange-500",
      general: "bg-gray-500",
    };
    return colors[type] || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <FolderOpen className="h-8 w-8 text-primary" />
            Document Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage templates, uploads, and automated document delivery
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Templates</p>
                <p className="text-2xl font-bold">{templates.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Uploaded Docs</p>
                <p className="text-2xl font-bold">{uploads.length}</p>
              </div>
              <Upload className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Auto-Send Active</p>
                <p className="text-2xl font-bold">
                  {scheduledDocs.filter((s) => s.is_active).length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="uploads" data-testid="tab-uploads">
            <Upload className="h-4 w-4 mr-2" />
            Uploads
          </TabsTrigger>
          <TabsTrigger value="auto-send" data-testid="tab-auto-send">
            <Clock className="h-4 w-4 mr-2" />
            Auto-Send
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Document Templates</CardTitle>
                <CardDescription>Create and manage reusable document templates</CardDescription>
              </div>
              <Button onClick={() => setTemplateDialogOpen(true)} data-testid="create-template-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No templates yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first template</p>
                  <Button onClick={() => setTemplateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell className="font-medium">{template.name}</TableCell>
                        <TableCell>
                          <Badge className={getTypeColor(template.type)}>{template.type}</Badge>
                        </TableCell>
                        <TableCell>
                          {template.is_active ? (
                            <Badge className="bg-green-500">Active</Badge>
                          ) : (
                            <Badge variant="outline">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {template.created_at
                            ? new Date(template.created_at).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTemplate(template)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Uploads Tab */}
        <TabsContent value="uploads" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Uploaded Documents</CardTitle>
                <CardDescription>Manage uploaded files and attachments</CardDescription>
              </div>
              <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-doc-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            </CardHeader>
            <CardContent>
              {uploads.length === 0 ? (
                <div className="text-center py-12">
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No documents uploaded</h3>
                  <p className="text-muted-foreground mb-4">Upload your first document</p>
                  <Button onClick={() => setUploadDialogOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploads.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{doc.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.category}</Badge>
                        </TableCell>
                        <TableCell>{doc.size || "-"}</TableCell>
                        <TableCell>
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Download original"
                              data-testid={`download-doc-${doc.id}`}
                              onClick={() => triggerDownload(`${API}/documents/uploads/${doc.id}/download`, doc.filename)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Download as PDF"
                              data-testid={`download-doc-pdf-${doc.id}`}
                              onClick={() => triggerDownload(`${API}/documents/uploads/${doc.id}/as-pdf`, `${doc.title || doc.filename || "document"}.pdf`)}
                            >
                              <FileType className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Download as Word (.docx)"
                              data-testid={`download-doc-docx-${doc.id}`}
                              onClick={() => triggerDownload(`${API}/documents/uploads/${doc.id}/as-docx`, `${doc.title || doc.filename || "document"}.docx`)}
                            >
                              <FileCode2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUpload(doc)}
                              className="text-destructive"
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Send Tab */}
        <TabsContent value="auto-send" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Automated Document Delivery</CardTitle>
                <CardDescription>Schedule automatic sending of documents</CardDescription>
              </div>
              <Button onClick={() => setAutoSendDialogOpen(true)} data-testid="create-auto-send-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Schedule
              </Button>
            </CardHeader>
            <CardContent>
              {scheduledDocs.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No scheduled sends</h3>
                  <p className="text-muted-foreground mb-4">Set up automatic document delivery</p>
                  <Button onClick={() => setAutoSendDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schedule
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Template</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Next Send</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduledDocs.map((schedule) => (
                      <TableRow key={schedule.id}>
                        <TableCell className="font-medium">
                          {schedule.template_name || schedule.template_id}
                        </TableCell>
                        <TableCell>{schedule.recipient_type}</TableCell>
                        <TableCell className="capitalize">{schedule.frequency}</TableCell>
                        <TableCell>
                          {schedule.next_send_date
                            ? new Date(schedule.next_send_date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {schedule.is_active ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <XCircle className="h-3 w-3 mr-1" />
                              Paused
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAutoSend(schedule)}
                          >
                            {schedule.is_active ? "Pause" : "Activate"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
            <DialogDescription>Create a new document template</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Standard Quote Template"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={templateForm.type}
                onValueChange={(v) => setTemplateForm({ ...templateForm, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">Quote</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="report">Report</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={templateForm.content}
                onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                placeholder="Template content with variables like {{client_name}}, {{date}}, etc."
                rows={8}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setTemplateDialogOpen(false);
                resetTemplateForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Template
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a new document to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>File *</Label>
              <Input
                type="file"
                accept={ACCEPTED_UPLOAD_TYPES}
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                required
                data-testid="upload-file-input"
              />
              <p className="text-xs text-muted-foreground">
                Accepted: PDF, Word (.doc/.docx), Text, Markdown, CSV, Excel, Images (max 10 MB)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Document title (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={uploadForm.category}
                onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="safety">Safety</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the document"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Auto-Send Dialog */}
      <Dialog open={autoSendDialogOpen} onOpenChange={setAutoSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Auto-Send Schedule</DialogTitle>
            <DialogDescription>Set up automated document delivery</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAutoSend} className="space-y-4">
            <div className="space-y-2">
              <Label>Template *</Label>
              <Select
                value={autoSendForm.template_id}
                onValueChange={(v) => setAutoSendForm({ ...autoSendForm, template_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recipients</Label>
              <Select
                value={autoSendForm.recipient_type}
                onValueChange={(v) => setAutoSendForm({ ...autoSendForm, recipient_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">All Clients</SelectItem>
                  <SelectItem value="employee">All Employees</SelectItem>
                  <SelectItem value="company">All Companies</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={autoSendForm.frequency}
                onValueChange={(v) => setAutoSendForm({ ...autoSendForm, frequency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Next Send Date</Label>
              <Input
                type="date"
                value={autoSendForm.next_send_date}
                onChange={(e) => setAutoSendForm({ ...autoSendForm, next_send_date: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAutoSendDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManagement;
